/**
 * Lift2Fit — sign in / create account.
 *
 * Public route: renders without the app shell. The two front doors are split so
 * the demos never blur the two products:
 *
 *   * `#/login` is the LIFTER login — lifter copy and one lifter demo. There is
 *     no coach or trainer demo here.
 *   * `#/crm/login` is the BUSINESS login — workspace copy, no demo at all. The
 *     workspace reads live data, so an offline coach demo would only reach
 *     honest empty reads; a coaching business reviews it with its own account.
 *
 * One credentials form, one error path, one session — only the words and the
 * secondary links change, so a coach who finds the wrong door is one link from
 * the right one.
 */
import { useEffect, useState } from 'react';
import { API_BASE, api, type User } from '@/lib/api';
import { cn } from '@/lib/format';
import { hasRole, readSession, useSession } from '@/lib/useSession';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui';
import { ArrowRightIcon, CheckIcon, LockIcon, SparkIcon } from '@/lib/icons';
import { LiveMessage, TrackIcon } from '../components';
import { TRACKS } from '../demoData';
import { Link, navigate } from '../router';
import { pendingInvitationCode } from '../crm/CrmJoinTeamScreen';
import { CONSENT_STATEMENT, CONSENT_SUBLINE, POLICY_VERSION } from '../legal/policy';

/** A coaching business lands in its workspace; everyone else on the tracker. */
/**
 * Where an account lands after signing in.
 *
 * ADMIN IS CHECKED FIRST. It used to check only `coach` and otherwise fall
 * through to `/dashboard`, so `admin@lift2fit.com` — which holds
 * `['admin','lifter']` — was sent to the lifter dashboard and had to reach the
 * admin portal by typing the URL. The portal is a third shell and, for an
 * account that holds the role, it is the home screen.
 */
export function homeFor(user: User | null): string {
  if (hasRole(user, 'admin')) return '/admin';
  return hasRole(user, 'coach') ? '/crm' : '/dashboard';
}

/** Which front door this is: the lifter app or the coaching workspace. */
export type LoginAudience = 'lifter' | 'crm';

/** The business login's promise, mirroring the CRM sign-up screen's checklist. */
const CRM_POINTS = [
  'Your roster, calendar, bookings and payments',
  'Programme reviews and AI risk flags across every client',
  'The lifter tracker is included on the same account',
];

export function LoginScreen({
  mode,
  audience = 'lifter',
}: {
  mode: 'login' | 'signup';
  audience?: LoginAudience;
}) {
  const session = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  // 0024: the consent checkbox starts UNTICKED, by law and by design. The API
  // refuses a signup without it; this state only spares the user a round trip.
  const [acceptedPolicy, setAcceptedPolicy] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [busy, setBusy] = useState(false);
  /**
   * Where to go after signing in. `?next=` is how an invitation link brings the
   * coach back to itself (the gym→team invitation, 0033). It is accepted only as
   * an internal path — a single leading slash, no scheme and no protocol-relative
   * `//` — so a crafted link cannot turn the login form into an open redirect.
   */
  const nextPath = (): string | null => {
    if (typeof window === 'undefined') return null;
    const raw = new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('next');
    if (raw === null) return null;
    const value = decodeURIComponent(raw);
    if (!value.startsWith('/') || value.startsWith('//') || value.includes('\\')) return null;
    return value;
  };
  const destination = (fallback: string): string =>
    // A pending team invitation outranks the usual landing spot: the coach
    // arrived from that link and the query does not survive navigation.
    (pendingInvitationCode() !== null ? '/crm/join-team' : null) ?? nextPath() ?? fallback;


  const [message, setMessage] = useState<string | null>(null);
  const [tone, setTone] = useState<'info' | 'danger' | 'success'>('info');

  // The business variant is a login only: `#/crm/signup` owns business sign-up.
  const business = audience === 'crm' && mode === 'login';

  useEffect(() => {
    // A coach who signs in goes straight to the CRM workspace; the tracker is
    // one link away in the header. Lifter accounts keep the dashboard: a lifter
    // who used the business door is not blocked, they simply land where they
    // belong, and the business login's secondary link says so before they do.
    if (session.isAuthenticated) navigate(destination(homeFor(session.user)), { replace: true });
  }, [session.isAuthenticated, session.user]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage(null);
    if (!email.includes('@')) {
      setTone('danger');
      setMessage('Enter the email address on your account.');
      return;
    }
    if (password.length < 8) {
      setTone('danger');
      setMessage('Passwords are at least 8 characters.');
      return;
    }
    if (mode === 'signup' && displayName.trim().length < 2) {
      setTone('danger');
      setMessage('Tell us what to call you.');
      return;
    }
    // The consent gate is server-side; this is the same refusal, shown at the
    // control, without a network round trip.
    if (mode === 'signup' && !acceptedPolicy) {
      setConsentError(true);
      setTone('danger');
      setMessage(`${CONSENT_STATEMENT} Tick the box to continue.`);
      return;
    }

    setBusy(true);
    setTone('info');
    setMessage(mode === 'login' ? 'Signing you in…' : 'Creating your account…');
    const result =
      mode === 'login'
        ? await session.login(email, password)
        : await session.register({
            email,
            password,
            displayName,
            inviteCode: inviteCode || undefined,
            acceptPolicy: true,
            policyVersion: POLICY_VERSION,
          });
    setBusy(false);

    if (!result.ok) {
      setTone('danger');
      setMessage(result.error ?? 'That did not work. Check your details and try again.');
      return;
    }
    setTone('success');
    setMessage('Signed in.');
    // `session.user` in this closure is one render behind; read the session the
    // API just published so a coach really lands in the workspace.
    navigate(destination(homeFor(readSession().user)));
  };

  const startDemo = () => {
    session.loginDemo('lifter');
    navigate('/dashboard');
  };

  return (
    <div className="l2f-app flex min-h-screen flex-col" data-testid={business ? 'crm-login' : 'lifter-login'}>
      <div className="mx-auto flex w-full max-w-[1120px] flex-1 flex-col items-center justify-center gap-10 px-4 py-10 md:flex-row md:items-stretch md:gap-16 md:px-8">
        {/* left: the promise */}
        <section className="flex w-full flex-col justify-center gap-6 md:max-w-[46%]">
          <Link to="/" aria-label="Lift2Fit home" className="flex items-center gap-3 text-ink">
            <Logo variant="horizontal" className="h-8 w-[150px]" />
          </Link>
          {business ? (
            <span className="inline-flex w-fit items-center gap-2 border border-hairline px-3 py-1 text-[10px] font-bold uppercase tracking-[0.1em] text-ink">
              <SparkIcon className="text-sm text-accent" /> Lift2Fit CRM
            </span>
          ) : null}
          <h1 className="font-display text-h2 font-black uppercase leading-[1.05] tracking-[-0.01em] text-ink md:text-display-2">
            {business
              ? 'Welcome back. Run your clients.'
              : mode === 'login'
                ? 'Welcome back. Log the work.'
                : 'Start free. No card.'}
          </h1>
          <p className="max-w-[52ch] text-[16px] text-ink-muted">
            {business
              ? 'Your workspace: clients and their programmes, your schedule and bookings, your payments — with AI assistance reading across every client. Built for coaches, performance gyms, studios and recovery centres.'
              : 'One log across web, phone and watch. Your coach reads it and writes next week — for the way you actually train.'}
          </p>
          <ul className="flex flex-col gap-2 text-[13px] text-ink-muted">
            {business
              ? CRM_POINTS.map((point) => (
                  <li key={point} className="flex items-start gap-2">
                    <CheckIcon className="mt-[2px] shrink-0 text-accent" />
                    <span className="leading-snug">{point}</span>
                  </li>
                ))
              : TRACKS.slice(0, 6).map((track) => (
                  <li key={track.id} className="flex items-center gap-2">
                    <TrackIcon track={track.id} className="text-[16px] text-accent" />
                    <span className="font-bold uppercase tracking-[0.06em] text-ink">{track.label}</span>
                    <span className="hidden sm:inline">— {track.description}</span>
                  </li>
                ))}
          </ul>
        </section>

        {/* right: the form */}
        <section className="flex w-full flex-col justify-center md:max-w-[420px]">
          <div className="border border-hairline bg-surface p-6 shadow-card">
            <h2 className="text-[13px] font-bold uppercase tracking-[0.1em] text-ink">
              {business ? 'Sign in to your workspace' : mode === 'login' ? 'Sign in' : 'Create your account'}
            </h2>
            <p className="mt-1 text-[13px] text-ink-muted">
              {business
                ? 'A coaching business — a solo coach, a studio or a gym — one login for the desk.'
                : mode === 'login'
                  ? 'Lifter or coach — the same login serves both.'
                  : 'You start as a lifter. A coaching business signs up at the CRM door.'}
            </p>

            <form className="mt-5 flex flex-col gap-4" onSubmit={submit} noValidate>
              {mode === 'signup' ? (
                <label className="flex flex-col gap-1.5">
                  <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Display name</span>
                  <input
                    type="text"
                    name="displayName"
                    autoComplete="name"
                    value={displayName}
                    onChange={(event) => setDisplayName(event.target.value)}
                    className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                  />
                </label>
              ) : null}

              <label className="flex flex-col gap-1.5">
                <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Email</span>
                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                />
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Password</span>
                <input
                  type="password"
                  name="password"
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                />
              </label>

              {mode === 'signup' ? (
                <label className="flex flex-col gap-1.5">
                  <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                    Coach invite code (optional)
                  </span>
                  <input
                    type="text"
                    name="inviteCode"
                    value={inviteCode}
                    onChange={(event) => setInviteCode(event.target.value)}
                    className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                  />
                </label>
              ) : null}

              {/* 0024 — the consent control. Never pre-ticked; the links open the
                  public policy pages (readable signed out) in the same tab. */}
              {mode === 'signup' ? (
                <div className="flex flex-col gap-1.5">
                  <label className="flex items-start gap-2.5">
                    <input
                      type="checkbox"
                      name="acceptPolicy"
                      checked={acceptedPolicy}
                      onChange={(event) => {
                        setAcceptedPolicy(event.target.checked);
                        if (event.target.checked) setConsentError(false);
                      }}
                      data-testid="signup-policy-checkbox"
                      data-policy-version={POLICY_VERSION}
                      aria-invalid={consentError}
                      aria-describedby={consentError ? 'signup-consent-error' : 'signup-consent-help'}
                      className="mt-[3px] h-4 w-4 shrink-0"
                    />
                    <span className="text-[13px] leading-snug text-ink-muted">
                      I have read and understand the{' '}
                      <Link
                        to="/legal/privacy"
                        onClick={(event) => event.stopPropagation()}
                        className="font-bold text-accent hover:underline"
                      >
                        Privacy Policy
                      </Link>
                      ,{' '}
                      <Link
                        to="/legal/terms"
                        onClick={(event) => event.stopPropagation()}
                        className="font-bold text-accent hover:underline"
                      >
                        Terms
                      </Link>{' '}
                      and{' '}
                      <Link
                        to="/legal/health-disclaimer"
                        onClick={(event) => event.stopPropagation()}
                        className="font-bold text-accent hover:underline"
                      >
                        Health Disclaimer
                      </Link>
                      .
                    </span>
                  </label>
                  <p id="signup-consent-help" className="pl-[26px] text-[12px] leading-snug text-ink-muted">
                    {CONSENT_SUBLINE}
                  </p>
                  {consentError ? (
                    <p
                      id="signup-consent-error"
                      role="alert"
                      data-testid="signup-consent-error"
                      className="pl-[26px] text-[12px] font-bold text-danger"
                    >
                      Tick “I have read and understand…” before creating your account.
                    </p>
                  ) : null}
                </div>
              ) : null}

              <Button
                type="submit"
                size="lg"
                disabled={busy}
                className="w-full"
                data-testid={mode === 'login' ? 'login-submit' : 'signup-submit'}
              >
                {busy ? 'Working…' : mode === 'login' ? 'Sign in' : 'Create account'}
                {!busy ? <ArrowRightIcon /> : null}
              </Button>
            </form>

            <LiveMessage message={message} tone={tone} />

            <p className="mt-2 flex items-center gap-1.5 text-[12px] text-ink-muted">
              <LockIcon className="text-[13px]" />
              Passwords are hashed with scrypt. Tokens stay on this device.
            </p>

            {/* The lifter demo belongs to the lifter login and nowhere else. The
                business login deliberately offers none: the workspace reads the
                API, so an offline coach demo would only reach honest empty
                reads. A coaching business reviews the workspace with its own
                account, and there is no trainer demo at all. */}
            {!business ? (
              <div className="mt-5 border-t border-hairline pt-4">
                <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                  No backend? Review the lifter app
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    data-testid="demo-signin"
                    onClick={startDemo}
                    aria-label="Continue in demo mode as a lifter"
                  >
                    Lifter demo
                  </Button>
                </div>
                <p className="mt-2 text-[12px] text-ink-muted">
                  Demo data — start the API to use your own. The demo track selector in the header
                  changes the numbers. Coaching businesses sign in at the business door with their own
                  account.
                </p>
              </div>
            ) : null}
          </div>

          {business ? (
            <>
              <p className="mt-4 text-center text-[13px] text-ink-muted">
                New to Lift2Fit CRM?{' '}
                <Link
                  to="/crm/signup"
                  data-testid="login-crm-signup-link"
                  className="font-bold text-accent hover:underline"
                >
                  Start free — one client, no card
                </Link>
              </p>
              <p className="mt-2 text-center text-[12px] text-ink-muted">
                Here for your own training?{' '}
                <Link to="/login" data-testid="login-lifter-link" className="font-bold text-accent hover:underline">
                  Lifter log in
                </Link>{' '}
                — you land on the tracker dashboard, not the workspace.
              </p>
            </>
          ) : (
            <>
              <p className="mt-4 text-center text-[13px] text-ink-muted">
                {mode === 'login' ? (
                  <>
                    New here?{' '}
                    <Link to="/signup" className="font-bold text-accent hover:underline">
                      Start free — no card
                    </Link>
                  </>
                ) : (
                  <>
                    Already training with us?{' '}
                    <Link to="/login" className="font-bold text-accent hover:underline">
                      Sign in
                    </Link>
                  </>
                )}
              </p>
              {mode === 'login' ? (
                <p className="mt-2 text-center text-[12px] text-ink-muted">
                  Running a coaching business?{' '}
                  <Link to="/crm/login" data-testid="login-crm-link" className="font-bold text-accent hover:underline">
                    Business log in
                  </Link>
                </p>
              ) : null}
            </>
          )}

          <p className="mt-2 text-center text-[12px] text-ink-muted">
            Service status:{' '}
            <span className={cn('font-bold', session.source === 'api' ? 'text-success' : 'text-ink-muted')}>
              {session.source === 'api' ? 'API reachable' : 'demo dataset'}
            </span>{' '}
            · <span className="l2f-tabular font-mono">{apiEndpoint()}</span>
          </p>
        </section>
      </div>
    </div>
  );
}

/** The endpoint actually in use — never a hard-coded localhost. */
function apiEndpoint(): string {
  return API_BASE.replace(/^https?:\/\//, '');
}
